import React, { useEffect, useRef, useState } from "react";
import { FaLinkedin, FaTiktok, FaYoutube, FaInstagram, FaFacebook } from 'react-icons/fa';
import SwoodleLogo from "../../Assets/Icons/SwoodleUpdatedLogo.svg";
import BlueRing from "../../Assets/Icons/bluering.svg";
import Flower from "../../Assets/Icons/flower.svg";
import ZigZag from "../../Assets/Icons/zigzagline.svg";
import EnvelopeIcon from "../../Assets/Icons/envelopeicon.svg";
import "./SwoodleFooter.scss";

// Reusable footer for Swoodle public-facing flows
// - Newsletter signup posts to REACT_APP_NEWSLETTER_SUBSCRIBE_URL if provided, else opens mail client
// - External links open in new tab

const DEFAULT_LINKS = {
  home: "https://swoodle.com.au/",
  contact: "https://swoodle.com.au/contact-us/",
  privacy: "https://swoodle.com.au/privacy-policy/",
  terms: "https://swoodle.com.au/terms-and-conditions/",
  merchantBenefits: "https://swoodle.com.au/merchant-feature/",
  fundraiser: "https://swoodle.com.au/fundraisers/",
  membersBenefits: "https://swoodle.com.au/members/",
  joinNow: "https://swoodle.com.au/join-now/",
  socials: {
    instagram: "https://www.instagram.com/swoodleau/",
    facebook: "https://www.facebook.com/swoodleau/",
    youtube: "https://www.youtube.com/@swoodleau",
    linkedin: "https://www.linkedin.com/company/swoodleau/",
    tiktok: "https://www.tiktok.com/@swoodleau",
  },
};

const NEWSLETTER_ENDPOINT = process.env.REACT_APP_NEWSLETTER_SUBSCRIBE_URL || "";

const SwoodleFooter = ({
  links = DEFAULT_LINKS,
  containerStyle,
  className = "",
  enableEntranceAnimation = false,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const footerRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enableEntranceAnimation) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        // Trigger as soon as the footer starts entering the viewport so the UI never looks empty
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          setInView(true);
        } else {
          setInView(false);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: [0, 0.1, 0.2, 0.4, 0.6, 0.8, 1],
      }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [enableEntranceAnimation]);

  useEffect(() => {
    if (!enableEntranceAnimation) {
      setInView(true);
    }
  }, [enableEntranceAnimation]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 0);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isValidEmail = (val) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(val);

  const onSubmitNewsletter = async (e) => {
    e?.preventDefault?.();
    setMessage("");
    if (!isValidEmail(email)) {
      setMessage("Please enter a valid email.");
      return;
    }
    // If an endpoint is configured, use it; otherwise open mail client as fallback
    if (NEWSLETTER_ENDPOINT) {
      try {
        setLoading(true);
        const resp = await fetch(NEWSLETTER_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(data?.message || "Subscription failed");
        setMessage("Thanks! You are subscribed.");
        setEmail("");
      } catch (err) {
        setMessage(err?.message || "Subscription failed. Please try again later.");
      } finally {
        setLoading(false);
      }
    } else {
      window.location.href = `mailto:info@swoodle.com.au?subject=Subscribe%20to%20Newsletter&body=Please%20subscribe%20${encodeURIComponent(
        email
      )}%20to%20the%20newsletter.`;
    }
  };

  const A = ({ href, children, className = "hover:underline" }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );

  // ZIGZAG LINE POSITION CONFIGURATION - Adjust strictly these values to move the line left (negative) or right (positive)
  const getZigzagLeft = () => {
    if (!viewportWidth) return null;
    if (viewportWidth <= 480) return "-190px";
    if (viewportWidth <= 640) return "-150px";
    if (viewportWidth <= 900) return "-110px";
    if (viewportWidth <= 1200) return "10px";
    if (viewportWidth <= 1300) return "2%";
    if (viewportWidth <= 1400) return "4%";
    if (viewportWidth <= 1550) return "2%";
    if (viewportWidth <= 1750) return "5%";
    return "12.5%"; // Position relative to width for very large screens
  };

  const zigzagStyle = { "--pop-delay": "0.05s" };
  const zigzagLeft = getZigzagLeft();
  if (zigzagLeft !== null) {
    zigzagStyle.left = zigzagLeft;
  }

  return (
    <footer
      ref={footerRef}
      className={[
        "swoodle-footer w-full max-w-[1450px] mx-auto bg-[#242856] text-white mt-10 rounded-[32px] overflow-visible relative",
        enableEntranceAnimation && inView ? "swoodle-footer--animated" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={containerStyle}
    >
      {/* Decorative assets per figma - kept above content via z-index and inside bounds */}
      <img
        src={ZigZag}
        alt=""
        className="swoodle-footer__decor pointer-events-none select-none absolute z-20 swoodle-footer__zigzag"
        style={zigzagStyle}
      />



      {/* Newsletter Section - Independent Container */}
      <div className="relative w-full px-4 sm:px-6 pt-8 sm:pt-10 md:pt-12 pb-4 sm:pb-6 md:pb-4">
        <div className="max-w-[1300px] mx-auto">
          <div className="swoodle-footer__newsletter relative z-10 rounded-[20px] md:rounded-[32px] bg-[#242856] p-6 md:p-8 lg:p-12">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,450px)_minmax(0,950px)] gap-4 md:gap-6 lg:gap-8 items-center">
              <div className="swoodle-footer__headline swoodle-footer__pop text-left -ml-5 md:ml-2" style={{ "--pop-delay": "0.1s" }}>
                <h3 className="text-[26px] sm:text-[32px] md:text-[40px] lg:text-[49px] leading-[1.32]" style={{ fontWeight: 300 }}>
                  Stay Updated with<br />
                  <span className="text-[#13A0E1] swoodle-footer__pop" style={{ "--pop-delay": "0.15s", fontWeight: 300 }}>Swoodle News</span>
                </h3>
              </div>
              <div className="swoodle-footer__cta swoodle-footer__pop text-left pt-6 md:pt-10" style={{ "--pop-delay": "0.2s" }}>
                <p className="text-[#FFFFFF] text-[14px] sm:text-[15px] md:text-[17px] relative -top-2 mb-3 md:mb-2 whitespace-normal lg:whitespace-nowrap -ml-4 md:-ml-0">
                  Sign up to receive the latest updates and offers from Swoodle.
                </p>
                <form
                  onSubmit={onSubmitNewsletter}
                  className="swoodle-footer__form flex flex-col sm:flex-row sm:items-center gap-3 md:gap-4 -ml-4 md:-ml-0 lg:-ml-0"
                >
                  <div className="flex-1 w-full">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="swoodle-footer__input w-full !h-[60px] rounded-[30px] px-3 md:px-4 bg-transparent border border-[#D6B05F] text-[#D6B05F] placeholder:text-[#B38E3F] text-[15px] md:text-[18px] outline-none"
                      autoComplete="email"
                      style={{ height: '60px' }}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="swoodle-footer__button w-full sm:w-auto sm:min-w-[200px] md:min-w-[240px] lg:min-w-[260px] !h-[60px] rounded-[30px] font-[600] text-[16px] md:text-[20px] disabled:opacity-70 px-8 md:px-10 whitespace-nowrap"
                    style={{ height: '60px' }}
                  >
                    <span>{loading ? "Submitting..." : "Join Our Newsletter"}</span>
                  </button>
                </form>
                {message && (
                  <div className="text-[11px] md:text-[12px] mt-2 text-[#C4C4C4]">{message}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Links Section - Independent Container */}
      <div className="relative w-full px-4 sm:px-6 pb-8 sm:pb-10 md:pb-8">
        <div className="max-w-[1180px] mx-auto">
          <div className="swoodle-footer__link-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[1.3fr_1fr_1fr_1fr] gap-8 md:gap-6 lg:gap-8 mt-2 md:mt-8 px-2 md:px-0">
            <div className="swoodle-footer__pop text-left md:-ml-2" style={{ "--pop-delay": "0.3s" }}>
              <A href={links.home} className="">
                <img src={SwoodleLogo} alt="Swoodle" className="w-[180px] sm:w-[200px] md:w-[220px] h-auto swoodle-footer__logo" />
              </A>
              <p className="text-[#FFFFFF] text-[15px] sm:text-[17px] md:text-[18px] mt-5 md:mt-6 !leading-[1.6]" style={{ maxWidth: viewportWidth >= 768 ? 'none' : '260px' }}>
                Grow your business, reach new<br className="hidden md:block" />
                customers. Fundraise with ease, and<br className="hidden md:block" />
                unlock exclusive deals and savings.
              </p>
              <div className="flex items-center justify-start gap-4 md:gap-6 mt-8 md:mt-9 ml-1 md:ml-2 swoodle-footer__socials relative top-1.5 overflow-visible">
                <A href={(links.socials && links.socials.instagram) || DEFAULT_LINKS.socials.instagram}><span className="sr-only">Instagram</span><FaInstagram size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
                <A href={(links.socials && links.socials.facebook) || DEFAULT_LINKS.socials.facebook}><span className="sr-only">Facebook</span><FaFacebook size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
                <A href={(links.socials && links.socials.youtube) || DEFAULT_LINKS.socials.youtube}><span className="sr-only">YouTube</span><FaYoutube size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
                <A href={(links.socials && links.socials.linkedin) || DEFAULT_LINKS.socials.linkedin}><span className="sr-only">LinkedIn</span><FaLinkedin size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
                <A href={(links.socials && links.socials.tiktok) || DEFAULT_LINKS.socials.tiktok}><span className="sr-only">TikTok</span><FaTiktok size={24} color="#FFC943" className="md:w-[26px] md:h-[26px]" /></A>
              </div>
            </div>
            <div className="md:pl-22 md:pt-1.5 swoodle-footer__pop text-left" style={{ "--pop-delay": "0.4s" }}>
              <h4 className="swoodle-footer__section-title mb-3 !text-[15px] md:!text-[17px] relative -top-1.5">QUICK LINKS</h4>
              <ul className="space-y-2 md:space-y-3 text-[#C4C4C4] text-[15px] md:text-[18px]">
                <li><A href={links.home} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">Home</A></li>


                <li><A href={links.contact} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">Contact Us</A></li>
                <li><A href={links.privacy} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">Privacy Policy</A></li>
                <li><A href={links.terms} className="hover:text-[#FFC943] hover:no-underline transition-colors duration-300">T&C's</A></li>
              </ul>
            </div>
            <div className="md:pl-3 md:pt-1.5 swoodle-footer__pop text-left" style={{ "--pop-delay": "0.5s" }}>
              <h4 className="swoodle-footer__section-title mb-3 !text-[15px] md:!text-[17px] relative -top-1.5">RESOURCES</h4>
              <ul className="space-y-2 md:space-y-3 text-[#C4C4C4] text-[15px] md:text-[18px]">
                <li><A href={links.membersBenefits} className="hover:text-[#FFC943] transition-colors duration-300">Memberships</A></li>
                <li><A href={links.merchantBenefits} className="hover:text-[#FFC943] transition-colors duration-300">Merchant Benefits</A></li>
                <li><A href={links.fundraiser} className="hover:text-[#FFC943] transition-colors duration-300">Fundraisers</A></li>
                <li><A href="https://swoodle.com.au/insights/" className="hover:text-[#FFC943] transition-colors duration-300">Insights</A></li>
              </ul>
            </div>
            <div className="swoodle-footer__pop text-left md:-ml-12" style={{ "--pop-delay": "0.6s" }}>
              <h4 className="swoodle-footer__section-title mb-3 !text-[15px] md:!text-[17px]">GET IN TOUCH</h4>
              <ul className="space-y-2 md:space-y-3 text-[#FFFFFF] text-[15px] md:text-[18px]">
                <li className="hover:text-[#FFC943] transition-colors duration-300 cursor-pointer flex items-start !mt-1 md:!mt-6">
                  <svg className="mr-1.5 mt-1 flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="#FFC943"><path d="M12 2C8.14 2 5 5.14 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.86-3.14-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" /></svg>
                  <div>
                    105a Glenferrie Rd Malvern,<br />Victoria, 3144, Australia
                  </div>
                </li>
                <li className="!mt-4 md:!mt-[18px]">
                  <svg className="inline mr-1.5 align-middle" width="22" height="22" viewBox="0 0 24 24" fill="#FFC943"><path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.21 11.72 11.72 0 003.67 1.17 1 1 0 01.88 1v3.09a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1h3.09a1 1 0 011 .88 11.72 11.72 0 001.17 3.67 1 1 0 01-.21 1.11l-2.43 2.13z" /></svg>
                  <a href="tel:+61401811115" className="hover:text-[#FFC943] transition-colors duration-300">0401 811 115</a>
                </li>
                <li className="!mt-3 md:!mt-[12px]">
                  <img src={EnvelopeIcon} alt="" className="inline mr-1.5 align-middle" width="20" height="20" />
                  <a href="mailto:info@swoodle.com.au" className="hover:text-[#FFC943] transition-colors duration-300">info@swoodle.com.au</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Divider and Copyright Section - Independent Container */}
      <div className="relative w-full px-4 sm:px-6 pb-6 md:pb-6 z-10 mt-20 md:mt-32">
        <div className="max-w-[1180px] mx-auto">
          {/* Divider line with decorative elements */}
          <div className="relative h-[1px] bg-[#D3D4DD] mb-5 w-full">
            {/* Three blue rings ABOVE divider */}
            <img
              src={BlueRing}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[147px] top-[-82px] w-[30px] z-20"
              style={{ "--pop-delay": "0.7s" }}
            />
            <img
              src={BlueRing}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[107px] top-[-82px] w-[30px] z-20"
              style={{ "--pop-delay": "0.8s" }}
            />
            <img
              src={BlueRing}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[68px] top-[-82px] w-[30px] z-20"
              style={{ "--pop-delay": "0.9s" }}
            />
            {/* Flower ON divider */}
            <img
              src={Flower}
              alt=""
              className="swoodle-footer__decor pointer-events-none select-none absolute right-[-7px] top-[-137px] w-[60px] z-20"
              style={{ "--pop-delay": "1s" }}
            />
          </div>
          {/* Copyright */}
          <div className="text-left text-[#AFB1C1] text-[14px] sm:text-[16px] md:text-[18px] font-medium">
            © {new Date().getFullYear()} Swoodle. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SwoodleFooter;


